import { booleanAttribute, ChangeDetectionStrategy, Component, computed, effect, inject, input } from '@angular/core';
import { LoadingSkeletonDirective2 } from 'common-ui';
import { ErrorComponent } from 'error-ui';
import { defaultItemPricingModel, ItemPriceComponentStore } from 'products-data';
import { CommonItemComponentBase } from '../../CommonItemComponentBase';
import { ItemPriceViewComponent } from './views';

@Component({
  selector: 'ideal-item-price',
  imports: [LoadingSkeletonDirective2, ItemPriceViewComponent, ErrorComponent],
  standalone: true,
  templateUrl: './item-price.component.html',
  styleUrl: './item-price.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [ItemPriceComponentStore],
  host: {
    '[attr.use-price-per]': 'usePricePer()',
    '[attr.sizing]': 'sizing()',
    '[attr.layout]': 'layout()',
    '[class.error]': 'hasError()',
    '[class.disable-call-for-price]': 'disableCallForPrice()',
    '[class.disable-sales]': 'disableSales()',
    '[class.disable-regular-price]': 'disableRegularPrice()',
    '[class.disable-units]': 'disableUnits()',
    '[class.disable-sales-notice]': 'disableSalesNotice()',
    '[class.disable-fees]': 'disableFees()',
    '[class.is-sale]': 'isSale()',
    '[class.is-limited-sale]': 'isSaleLimited()',
    '[class.has-fees]': 'hasFees()',
    '[class.has-notice]': 'hasNotice()',
    '[class.no-price]': 'ready() &&!hasPrice()',
  },
})
export class ItemPriceComponent extends CommonItemComponentBase {
  public readonly usePricePer = input<string | undefined>(undefined);
  public readonly multiplyBy = input(1);
  public readonly layout = input<'stacked' | 'inline' | 'horizontal'>('stacked');
  public readonly align = input<'start' | 'end' | 'center'>('end');
  public readonly disableCallForPrice = input(false, { transform: booleanAttribute });
  public readonly disableUnits = input(false, { transform: booleanAttribute });
  public readonly disableSales = input(false, { transform: booleanAttribute });
  public readonly disableRegularPrice = input(false, { transform: booleanAttribute });
  public readonly disableSalesNotice = input(false, { transform: booleanAttribute });
  public readonly sizing = input<'compact' | 'normal' | 'fixed'>('normal');
  public readonly disableFees = input(false, { transform: booleanAttribute });

  private readonly _priceStore = inject(ItemPriceComponentStore);
  protected readonly pricing = computed(() => this._priceStore.itemPricing()?.pricing ?? defaultItemPricingModel);

  protected readonly priceLoading = computed(() => this._priceStore.itemPricing.loading());
  protected readonly ready = computed(
    () => this._priceStore.itemPricing.error() || (!this._priceStore.itemPricing.loading() && this._priceStore.itemPricing()),
  );
  protected readonly hasError = computed(() => !this._priceStore.itemPricing.loading() && !!this._priceStore.itemPricing.error());
  protected readonly error = computed(() => this._priceStore.itemPricing.error());

  protected readonly hasPrice = computed(() => this.pricing()?.hasPrice ?? false);

  protected readonly isCallForPrice = computed(
    () => !this.disableCallForPrice() && (this.pricing() ? this.pricing()!.isCallForPricing : false),
  );
  protected readonly isSale = computed(() => !this.disableSales() && (this.pricing()?.isOnSale ?? false));
  protected readonly isSaleLimited = computed(
    () => !this.disableSales() && !this.disableSalesNotice() && (this.pricing()?.isOnSaleLimited ?? false),
  );

  protected readonly fees = computed(() => this.pricing()?.fees ?? []);
  protected readonly hasFees = computed(() => !this.disableFees() && this.fees().length > 0);

  protected readonly hasNotice = computed(() => this.hasFees() || this.isSaleLimited());

  protected readonly usePricePerPrimary = computed(() => {
    let usePricePer = this.usePricePer() ?? '';

    const usePricePerParts = usePricePer.split('+');
    usePricePer = usePricePerParts[0];

    switch (usePricePer) {
      case 'unit':
      case 'auto':
      case 'container':
      case '':
      case undefined:
        break;
      case 'sold-in':
        const quantitySoldIn = this.pricing()?.quantitySoldIn ?? 1;
        const quantityPerContainer = this.pricing()?.quantityPerContainer ?? 1;
        if (quantitySoldIn > 1 && quantitySoldIn === quantityPerContainer) {
          usePricePer = 'container';
        } else {
          usePricePer = 'sold-in';
        }
        break;
      default:
        usePricePer = 'sold-in';
    }

    // if (usePricePer === 'unit+container') {
    //   return 'unit';
    // }
    // if (usePricePer === 'sold-in+container') {
    //   const quantitySoldIn = this.pricing()?.quantitySoldIn ?? 1;
    //   const quantityPerContainer = this.pricing()?.quantityPerContainer ?? 1;
    //   return quantitySoldIn === quantityPerContainer ? 'unit' : 'sold-in';
    // }
    return usePricePer as 'unit' | 'sold-in' | 'container' | 'auto' | '' | undefined;
  });

  protected readonly usePricePerSecondary = computed(() => {
    const usePricePerParts = (this.usePricePer() ?? '').split('+');
    const usePricePer = this.usePricePerPrimary();
    const usePricePerSecond = usePricePerParts[1];

    if (usePricePerSecond === 'container' && usePricePer !== 'container') {
      const quantityPerContainer = this.pricing()?.quantityPerContainer ?? 1;
      const hasContainer = quantityPerContainer > 1;
      if (hasContainer) {
        return 'container';
      }
    }
    return undefined;
  });

  constructor() {
    super();
    effect(() => this._priceStore.selectItemById(this.itemId()));
  }
}
